import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Segmented, Select, Input, InputNumber, Button, Space, Typography, App, Alert, Image } from 'antd';
import { ThunderboltOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useMediaModels, useGenerate, useGenerationPoll } from '@/hooks/useMedia';
import type { MediaInput, TaskType } from '@/types/media';
import { ProgramRefPicker, type ProgramPick } from './ProgramRefPicker';

const TASK_OPTIONS = [
  { label: 'Image', value: 't2i' },
  { label: 'Image + ref', value: 'i2i' },
  { label: 'Video', value: 't2v' },
  { label: 'Video from image', value: 'i2v' },
];
const NEEDS_INPUTS = (t: TaskType) => t === 'i2i' || t === 'i2v';

export function GeneratePanel({ onGenerated, allowedTasks, initialTopicNo }: {
  onGenerated: (id: string) => void;
  allowedTasks?: TaskType[];
  initialTopicNo?: string;
}) {
  const { message } = App.useApp();
  const modelsQ = useMediaModels();
  const gen = useGenerate();
  const taskOptions = useMemo(
    () => (allowedTasks ? TASK_OPTIONS.filter((o) => allowedTasks.includes(o.value as TaskType)) : TASK_OPTIONS),
    [allowedTasks],
  );
  const [taskType, setTaskType] = useState<TaskType>((taskOptions[0]?.value as TaskType) ?? 't2i');
  const [model, setModel] = useState<string>('');
  const [prompt, setPrompt] = useState('');
  const [negative, setNegative] = useState('');
  const [count, setCount] = useState(1);
  const [size, setSize] = useState<string>('');
  const [resolution, setResolution] = useState<string>('');
  const [duration, setDuration] = useState<number | undefined>();
  const [inputs, setInputs] = useState<string[]>([]);       // reference image URLs
  const [programImages, setProgramImages] = useState<string[]>([]);
  const [sourceTopicNo, setSourceTopicNo] = useState<string | undefined>(initialTopicNo);
  const [pasteUrl, setPasteUrl] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  const poll = useGenerationPoll(jobId, !!jobId);
  const running = poll.data?.status === 'running' || poll.data?.status === 'queued';

  // When the async job completes, refresh the library once (and stop tracking).
  const notified = useRef<string | null>(null);
  useEffect(() => {
    const s = poll.data?.status;
    if (!jobId || !s) return;
    if ((s === 'succeeded' || s === 'failed' || s === 'canceled') && notified.current !== jobId) {
      notified.current = jobId;
      if (s === 'succeeded') { onGenerated(jobId); message.success('Generation finished'); setJobId(null); }
    }
  }, [poll.data?.status, jobId, onGenerated, message]);

  const models = (modelsQ.data ?? []).filter((m) => m.enabled && m.taskType === taskType);
  const current = models.find((m) => m.model === model) ?? models[0];
  const schema = (current?.paramsSchema ?? {}) as { sizes?: string[]; resolutions?: string[]; durations?: number[]; max_count?: number; max_inputs?: number };
  const maxInputs = schema.max_inputs ?? (taskType === 'i2v' ? 1 : 4);
  const effDuration = duration ?? schema.durations?.[0] ?? 5;
  const isVideo = taskType === 't2v' || taskType === 'i2v';

  const cost = useMemo(() => {
    if (!current) return 0;
    if (current.unit === 'per_second') return current.unitPrice * effDuration;
    return current.unitPrice * count;
  }, [current, count, effDuration]);

  const addInput = (url: string) => {
    setInputs((prev) => {
      if (prev.includes(url)) return prev;
      const next = [...prev, url];
      return next.slice(-maxInputs); // keep the newest, capped (i2v keeps 1)
    });
  };

  const onProgramPick = (p: ProgramPick) => {
    setSourceTopicNo(p.topicNo);
    setProgramImages(p.images);
    if (!prompt.trim()) setPrompt(p.promptSeed);
  };

  const submit = async () => {
    if (!current) { message.warning('No enabled model for this task type'); return; }
    if (!prompt.trim()) { message.warning('Enter a prompt'); return; }
    if (NEEDS_INPUTS(taskType) && inputs.length === 0) { message.warning('Pick at least one reference image'); return; }
    const params: Record<string, unknown> = {};
    if (!isVideo) { params.count = count; if (size) params.size = size; }
    else { params.duration = effDuration; if (size) params.size = size; if (resolution) params.resolution = resolution; }
    const wireInputs: MediaInput[] = NEEDS_INPUTS(taskType)
      ? inputs.map((url) => ({ url, role: taskType === 'i2v' ? 'first_frame' : 'reference' }))
      : [];
    try {
      const row = await gen.mutateAsync({ model: current.model, prompt, negativePrompt: negative || undefined, inputs: wireInputs.length ? wireInputs : undefined, params, sourceTopicNo });
      if (row.status === 'running' || row.status === 'queued') setJobId(row.id);
      else setJobId(null);
      onGenerated(row.id);
      message.success(row.status === 'succeeded' ? 'Generated' : 'Job started');
    } catch (e) { message.error((e as Error).message); }
  };

  return (
    <Card title="Generate">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Segmented value={taskType} onChange={(v) => { setTaskType(v as TaskType); setModel(''); setInputs([]); setSize(''); setResolution(''); setDuration(undefined); setCount(1); }} options={taskOptions} block />
        <ProgramRefPicker initialTopicNo={initialTopicNo} onPick={onProgramPick} onClear={() => { setSourceTopicNo(undefined); setProgramImages([]); }} />
        <Select style={{ width: '100%' }} placeholder="Model" value={current?.model} onChange={setModel}
          options={models.map((m) => ({ value: m.model, label: `${m.model} · $${m.unitPrice}/${m.unit.replace('per_', '')}` }))} />
        {modelsQ.isError && <Alert type="error" showIcon message={(modelsQ.error as Error).message} />}
        <Input.TextArea rows={3} placeholder="Prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <Input placeholder="Negative prompt (optional)" value={negative} onChange={(e) => setNegative(e.target.value)} />

        {NEEDS_INPUTS(taskType) && (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Reference images ({inputs.length}/{maxInputs}) — click a program image or paste a URL
            </Typography.Text>
            {programImages.length > 0 && (
              <Space wrap size={4}>
                {programImages.map((u) => (
                  <Image key={u} src={u} width={56} height={56} style={{ objectFit: 'cover', cursor: 'pointer', border: inputs.includes(u) ? '2px solid #1677ff' : '1px solid #ddd', borderRadius: 4 }}
                    preview={false} onClick={() => addInput(u)} />
                ))}
              </Space>
            )}
            <Space.Compact style={{ width: '100%' }}>
              <Input size="small" placeholder="https://… image URL" value={pasteUrl} onChange={(e) => setPasteUrl(e.target.value)} />
              <Button size="small" onClick={() => { if (/^https?:\/\//.test(pasteUrl)) { addInput(pasteUrl.trim()); setPasteUrl(''); } else { message.warning('Enter an http(s) URL'); } }}>Add</Button>
            </Space.Compact>
            <Space wrap size={4}>
              {inputs.map((u) => (
                <span key={u} style={{ position: 'relative', display: 'inline-block' }}>
                  <Image src={u} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} preview={false} />
                  <CloseCircleFilled style={{ position: 'absolute', top: -6, right: -6, color: '#999', cursor: 'pointer' }}
                    onClick={() => setInputs((prev) => prev.filter((x) => x !== u))} />
                </span>
              ))}
            </Space>
          </Space>
        )}

        {schema.sizes && <Select style={{ width: '100%' }} placeholder="Size" value={size || undefined} onChange={setSize} options={schema.sizes.map((s) => ({ value: s, label: s }))} allowClear />}
        {schema.resolutions && <Select style={{ width: '100%' }} placeholder="Resolution" value={resolution || undefined} onChange={setResolution} options={schema.resolutions.map((r) => ({ value: r, label: r }))} allowClear />}
        {!isVideo
          ? <Space>Count <InputNumber min={1} max={schema.max_count ?? 4} value={count} onChange={(v) => setCount(v ?? 1)} /></Space>
          : <Space>Duration (s) <Select value={effDuration} onChange={setDuration} options={(schema.durations ?? [5, 10]).map((d) => ({ value: d, label: `${d}s` }))} style={{ width: 100 }} /></Space>}
        <Typography.Text type="secondary">Est. cost: ${cost.toFixed(3)}</Typography.Text>
        <Button type="primary" icon={<ThunderboltOutlined />} loading={gen.isPending || running} onClick={submit} disabled={!current}>Generate</Button>
        {jobId && running && <Alert type="info" showIcon message={`Job ${poll.data?.status ?? 'running'}… (keeps running if you close the tab)`} />}
        {jobId && poll.data?.status === 'failed' && <Alert type="error" showIcon message={poll.data.error ?? 'Generation failed'} />}
      </Space>
    </Card>
  );
}
